import { useCallback, useEffect, useMemo } from 'react';
import { useOutsideFrameMessenger, useInsideFrameMessenger, simpleStateSetHandler } from './messengerHooks';
const staticStateMessageKey = 'staticState';
const contentMessageKey = 'content';
const errorMessageKey = 'error';
const noContentMessageKey = 'noContent';
const videoPlayMessageKey = 'videoPlay';
const videoFinishMessageKey = 'videoFinish';
const videoEngagementMessageKey = 'videoEngagement';
const videoProgressMessageKey = 'videoProgress';
const watermarkLinkClickMessageKey = 'watermarkLinkClick';

// Recommendations Widget keys
const contentErrorMessageKey = 'contentError';
const cardClickMessageKey = 'cardClick';
const contentLoadMessageKey = 'contentLoad';
export const useSyncStaticState = ({
  newState,
  onContent,
  onError,
  onNoContent,
  onVideoPlay,
  onVideoFinish,
  onVideoEngagement,
  onVideoProgress,
  onWatermarkClick
}) => {
  const messageHandlers = useMemo(() => ({
    [contentMessageKey]: message => {
      onContent === null || onContent === void 0 || onContent(message);
    },
    [errorMessageKey]: () => {
      onError === null || onError === void 0 || onError();
    },
    [noContentMessageKey]: () => {
      onNoContent === null || onNoContent === void 0 || onNoContent();
    },
    [videoPlayMessageKey]: () => {
      onVideoPlay === null || onVideoPlay === void 0 || onVideoPlay();
    },
    [videoFinishMessageKey]: () => {
      onVideoFinish === null || onVideoFinish === void 0 || onVideoFinish();
    },
    [videoEngagementMessageKey]: () => {
      onVideoEngagement === null || onVideoEngagement === void 0 || onVideoEngagement();
    },
    [videoProgressMessageKey]: progress => {
      onVideoProgress === null || onVideoProgress === void 0 || onVideoProgress(progress);
    },
    [watermarkLinkClickMessageKey]: () => {
      onWatermarkClick === null || onWatermarkClick === void 0 || onWatermarkClick();
    }
  }), [onContent, onError, onNoContent, onVideoEngagement, onVideoFinish, onVideoPlay, onVideoProgress, onWatermarkClick]);
  const {
    registerIFrame,
    sendMessage,
    isReady
  } = useOutsideFrameMessenger({
    key: 'static',
    messageHandlers
  });
  useEffect(() => {
    if (newState && sendMessage && isReady) {
      sendMessage(staticStateMessageKey, newState);
    }
  }, [isReady, newState, sendMessage]);
  return {
    registerIFrame
  };
};
const syncStaticInsideHandlers = {
  [staticStateMessageKey]: simpleStateSetHandler
};
export const useSyncStaticStateInsideIFrame = () => {
  const {
    state,
    sendMessage
  } = useInsideFrameMessenger({
    key: 'static',
    messageHandlers: syncStaticInsideHandlers
  });
  const onContent = useCallback(content => {
    sendMessage(contentMessageKey, content);
  }, [sendMessage]);
  const onError = useCallback(() => {
    sendMessage(errorMessageKey, {});
  }, [sendMessage]);
  const onNoContent = useCallback(() => {
    sendMessage(noContentMessageKey, {});
  }, [sendMessage]);
  const onVideoPlay = useCallback(() => {
    sendMessage(videoPlayMessageKey, {});
  }, [sendMessage]);
  const onVideoFinish = useCallback(() => {
    sendMessage(videoFinishMessageKey, {});
  }, [sendMessage]);
  const onVideoEngagement = useCallback(() => {
    sendMessage(videoEngagementMessageKey, {});
  }, [sendMessage]);
  const onVideoProgress = useCallback(progress => {
    sendMessage(videoProgressMessageKey, progress);
  }, [sendMessage]);
  const onWatermarkClick = useCallback(() => {
    sendMessage(watermarkLinkClickMessageKey, {});
  }, [sendMessage]);
  return {
    state,
    onContent,
    onError,
    onNoContent,
    onVideoPlay,
    onVideoFinish,
    onVideoEngagement,
    onVideoProgress,
    onWatermarkClick
  };
};
export const useSyncRecommendationsProps = ({
  props,
  onCardClick,
  onContentError,
  onContentLoad
}) => {
  const messageHandlers = useMemo(() => ({
    [cardClickMessageKey]: card => {
      onCardClick === null || onCardClick === void 0 || onCardClick(card);
    },
    [contentLoadMessageKey]: content => {
      onContentLoad === null || onContentLoad === void 0 || onContentLoad(content);
    },
    [contentErrorMessageKey]: error => {
      onContentError === null || onContentError === void 0 || onContentError(error);
    }
  }), [onContentLoad, onCardClick, onContentError]);
  const {
    registerIFrame,
    sendMessage,
    isReady
  } = useOutsideFrameMessenger({
    key: 'static',
    messageHandlers
  });
  useEffect(() => {
    if (isReady) {
      sendMessage === null || sendMessage === void 0 || sendMessage(staticStateMessageKey, props);
    }
  }, [isReady, sendMessage, props]);
  return {
    registerIFrame,
    sendMessage,
    isReady
  };
};
export const useSyncRecommendationsPropsInsideIFrame = () => {
  const {
    state: props,
    sendMessage
  } = useInsideFrameMessenger({
    key: 'static',
    messageHandlers: syncStaticInsideHandlers
  });
  const onCardClick = useCallback(card => {
    sendMessage(cardClickMessageKey, card);
  }, [sendMessage]);
  const onContentLoad = useCallback(content => {
    sendMessage(contentLoadMessageKey, content);
  }, [sendMessage]);
  const onContentError = useCallback(error => {
    sendMessage(contentErrorMessageKey, error);
  }, [sendMessage]);
  return {
    props,
    sendMessage,
    onCardClick,
    onContentLoad,
    onContentError
  };
};